/**
 * DOLLARS, WRITTEN THE WAY PEOPLE READ THEM — one helper for every loyalty
 * screen. 1,255.50 in English; French and Spanish get their own separators,
 * always grouped. Points are never touched by this file.
 *
 * `money()` is for showing an amount. `typeMoney()` is for a field someone is
 * typing in: it groups as they type, keeps the decimals they have started, and
 * hands back where the cursor belongs. `plain()` is what goes to the server —
 * a bare number with no separators.
 */
import i18n from './i18n';

const localeOf = (lang) => {
    const code = String(lang || i18n.language || 'en').slice(0, 2);
    return { fr: 'fr-CA', es: 'es-ES' }[code] || 'en-CA';
};

const partsOf = (lang) => {
    const bits = new Intl.NumberFormat(localeOf(lang), { minimumFractionDigits: 2 })
        .formatToParts(1234.5);
    return {
        group: (bits.find((b) => b.type === 'group') || {}).value || ',',
        decimal: (bits.find((b) => b.type === 'decimal') || {}).value || '.',
    };
};

/** An amount to show: "$1,255.50". */
export const money = (value, lang) => {
    const n = Number(value);
    const safe = Number.isFinite(n) ? n : 0;
    return `$${new Intl.NumberFormat(localeOf(lang), {
        minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(safe)}`;
};

/** The same number without the dollar sign, for sentences that add their own. */
export const amount = (value, lang) => money(value, lang).slice(1);

/** What the server receives: "1255.5", never "1,255.5". */
export const plain = (typed) => {
    const text = String(typed ?? '');
    if (!text) return '';
    const { decimal } = partsOf();
    const cleaned = text.replace(new RegExp(`[^0-9${decimal === '.' ? '.' : ','}\\-]`, 'g'), '')
        .replace(decimal, '.');
    return cleaned;
};

/**
 * Group what is being typed. Returns the text for the field and the caret
 * position, so the cursor stays where the person put it.
 */
export const typeMoney = (raw, caret, lang) => {
    const { group, decimal } = partsOf(lang);
    const text = String(raw ?? '');
    const digitsBefore = text.slice(0, caret ?? text.length)
        .replace(new RegExp(`[^0-9${decimal === '.' ? '\\.' : ','}]`, 'g'), '').length;

    let kept = '';
    let seenDecimal = false;
    for (const ch of text) {
        if (ch >= '0' && ch <= '9') kept += ch;
        else if ((ch === decimal || ch === '.' || ch === ',') && !seenDecimal && kept) {
            // The first separator the person types is their decimal point.
            if (ch === group && ch !== decimal) continue;
            kept += '.';
            seenDecimal = true;
        }
    }
    if (!kept) return { text: '', caret: 0 };

    const [whole, fraction] = kept.split('.');
    const grouped = whole
        ? new Intl.NumberFormat(localeOf(lang), { maximumFractionDigits: 0 })
            .format(Number(whole))
        : '';
    const out = seenDecimal
        ? `${grouped}${decimal}${(fraction || '').slice(0, 2)}`
        : grouped;

    // Put the caret back after the same number of digits the person had typed.
    let seen = 0;
    let at = out.length;
    for (let i = 0; i < out.length; i += 1) {
        const ch = out[i];
        if ((ch >= '0' && ch <= '9') || ch === decimal) seen += 1;
        if (seen >= digitsBefore) { at = i + 1; break; }
    }
    return { text: out, caret: at };
};

/** Props for a plain <input>/<Input> that formats dollars as it is typed. */
export const moneyFieldProps = (value, onPlainChange, lang) => ({
    inputMode: 'decimal',
    value: value ?? '',
    onChange: (e) => {
        const { text, caret } = typeMoney(e.target.value, e.target.selectionStart, lang);
        const el = e.target;
        onPlainChange(text);
        window.requestAnimationFrame(() => {
            try { el.setSelectionRange(caret, caret); } catch { /* not a text field */ }
        });
    },
    // Leaving the field settles the cents: 1,255.5 becomes 1,255.50.
    onBlur: () => {
        const bare = plain(value);
        if (bare !== '' && Number.isFinite(Number(bare))) onPlainChange(amount(bare, lang));
    },
});
